import { http } from '@uhg-abyss/web/tools/http';
import { FLOW_ID } from '@src/common/AppConstants';
import { event as eapmEvent } from '@uhg-abyss/web/tools/event';
import { getSourceSystem } from '../utility';

const DESIGNATE_PAA_URL_MAP = {
  [FLOW_ID.PROVIDER]: '/provider/designate/v1',
  [FLOW_ID.BILLING]: '/billing/designate/v1',
  [FLOW_ID.VENDOR]: '/vendor/designate/v1',
  [FLOW_ID.API_MARKETPLACE_PROVIDER]: '/ext/provider/designate/v1',
  [FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN]: '/ext/provider/designate/v1',
  [FLOW_ID.API_MARKETPLACE_BILLING]: '/ext/billing/designate/v1',
  [FLOW_ID.API_MARKETPLACE_VENDOR]: '/ext/vendor/designate/v1',
};

const getDesignatePAARequestPayload = (payload) => {
  const { currentFlow } = payload;

  if (
    currentFlow === FLOW_ID.PROVIDER ||
    currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER ||
    currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN ||
    currentFlow === FLOW_ID.API_MARKETPLACE_BILLING ||
    currentFlow === FLOW_ID.API_MARKETPLACE_VENDOR
  )
    return {
      requestId: payload.requestId,
      designatePaaFirstName: payload.firstName,
      designatePaaLastName: payload.lastName,
      designatePaaEmail: payload.email,
    };

  if (currentFlow === FLOW_ID.BILLING || currentFlow === FLOW_ID.VENDOR)
    return {
      requestId: payload.requestId,
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
    };

  return '';
};

export const designatePAA = async (payload) => {
  const { isDental, ...restPayload } = payload;
  const apiPayload = getDesignatePAARequestPayload(restPayload);
  const sourceSystem = getSourceSystem(payload.currentFlow);

  const response = await http({
    method: 'POST',
    cache: false,
    withCredentials: true,
    url: `${payload.url}${
      isDental
        ? '/ext/dental/designate/v1'
        : DESIGNATE_PAA_URL_MAP[payload.currentFlow]
    }`,
    headers: {
      sourceSystem,
    },
    handleError: async (err) => {
      const { error } = err.data;
      const { duration, endpoint, message, data, status } = err;
      const errorData = {
        status: data?.error?.code,
        message: data?.error?.description || message,
        code: status,
        duration,
        endpoint,
      };
      eapmEvent('EAPM_TRANSACTION_ERROR', errorData);
      const errObj = {
        error: { ...error, status: err.status },
      };
      return {
        data: {
          ...errObj,
        },
      };
    },
    body: apiPayload,
  });

  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);

  return {
    ...data,
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
  };
};

const getOrgType = (currentFlow) => {
  if (currentFlow.includes('provider')) return 'provider';
  if (currentFlow.includes('billing')) return 'billing';
  if (currentFlow.includes('vendor')) return 'vendor';
  return '';
};

const getAPITechnicianRequest = (payload) => {
  const providerFields =
    payload.currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN
      ? {
          jobFunctions: payload.jobFunctions,
        }
      : {};
  return {
    requestId: payload.requestId,
    phoneNumber: payload.phoneNumber,
    phoneExt: payload.phoneExt,
    ...providerFields,
  };
};

const getAPISecretKeeperRequest = (payload) => {
  const providerFields =
    payload.currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER
      ? {
          phoneNumber: payload.phoneNumber,
          phoneExt: payload.phoneExt,
          jobFunctions: payload.jobFunctions,
        }
      : {};
  const billingVendorFields =
    payload.currentFlow !== FLOW_ID.API_MARKETPLACE_PROVIDER
      ? {
          businessName: payload.businessName,
          phoneNumber: payload.phoneNumber,
          phoneExt: payload.phoneExt,
        }
      : {};
  return {
    requestId: payload.requestId,
    ...providerFields,
    ...billingVendorFields,
    apiRequested: payload.apiRequested,
    comments: payload.comments,
  };
};

export const submitTINRequest = async (payload) => {
  const isAPIMarketplaceFlow = payload.currentFlow.includes('API marketplace');
  const isAPITechnician = payload.currentFlow.includes('technician');
  const sourceSystem = isAPIMarketplaceFlow ? { sourceSystem: 'apim' } : {};

  let apiPath = 'provider/paa/submit/v1';
  let requestPayload = { ...payload };

  if (isAPIMarketplaceFlow) {
    const orgType = payload.isDental
      ? 'dental'
      : getOrgType(payload.currentFlow);
    if (isAPITechnician) {
      apiPath = `ext/${orgType}/submit/v1`;
      requestPayload = getAPITechnicianRequest(payload);
    } else {
      apiPath = `ext/${orgType}/paa/submit/v1`;
      requestPayload = getAPISecretKeeperRequest(payload);
    }
  }

  const { isDental, ...apiPayload } = requestPayload;
  const response = await http({
    method: 'POST',
    cache: false,
    withCredentials: true,
    body: apiPayload,
    headers: { ...sourceSystem },
    url: `${payload.url}/${apiPath}`,
    handleError: async (err) => {
      const { error } = err.data;
      const { duration, endpoint, message, data, status } = err;
      const errorData = {
        status: data?.error?.code,
        message: data?.error?.description || message,
        code: status,
        duration,
        endpoint,
      };
      eapmEvent('EAPM_TRANSACTION_ERROR', errorData);
      const errObj = {
        error: { ...error, status: err.status },
      };
      return {
        data: {
          ...errObj,
        },
      };
    },
  });
  const { duration, endpoint, status, data } = response;
  const successData = {
    endpoint,
    duration,
    status,
  };
  eapmEvent('EAPM_TRANSACTION_SUCCESS', successData);
  return data;
};
